// The modifier options of a cart line with how many times each counts per unit of the line,
// in the order the API lists `modifier_prices` (CartPriceGuard::_modifierPricesOf): every
// option of modifier_group, each followed by its own item_in_mog options, then for a Choice
// item the options of each choice_selected.items[].item_detail.
const modifierOptionsOf = (item) => {
  const options = [];
  const walk = (groups, multiplier) => {
    (groups || []).forEach((group) => {
      (group.items || []).forEach((option) => {
        const quantity = parseFloat(option.quantity ?? 1) || 0;
        options.push({ option, count: quantity * multiplier });
        walk(option.item_in_mog && option.item_in_mog.modifier_group, quantity * multiplier);
      });
    });
  };
  walk(item.modifier_group, 1);
  ((item.choice_selected && item.choice_selected.items) || []).forEach((choiceItem) => {
    walk(choiceItem.item_detail && choiceItem.item_detail.modifier_group, parseFloat(choiceItem.qty ?? 1) || 0);
  });
  return options;
};

// Moves the line's modifier options onto `modifier_prices` (a modifier section's scheduled
// price list starting or ending) and returns how much one unit of the line changes by. A list
// that does not line up with the line is left alone (0).
const applyModifierPrices = (item, modifierPrices, round) => {
  const options = modifierOptionsOf(item);
  if (!Array.isArray(modifierPrices) || modifierPrices.length !== options.length) {
    return 0;
  }

  let delta = 0;
  options.forEach(({ option, count }, index) => {
    const next = round(Number(modifierPrices[index].price) || 0);
    const previous = parseFloat(option.price) || 0;
    if (Math.abs(next - previous) < 0.005) {
      return;
    }
    delta += (next - previous) * count;
    option.price = next;
    option.price_default = next;
    option.price_format = modifierPrices[index].price_format;
    option.price_default_format = modifierPrices[index].price_format;
  });
  return round(delta);
};

// Price Schedules: reprice a cart kept past a price change (a schedule starting or ending)
// with the prices order/reload returns (`prices`, QR requests), keyed like the cart lines.
// A line's base (no-modifier) price moves with the Main Item schedules, and its modifier
// options with the modifier sections' scheduled price lists (`modifier_prices`). Mutates the
// lines; returns one entry per line that changed: { item, from, to }, prices per unit as shown
// (member price when isMember), so the cart can highlight those lines and list them
// (ModalPricesUpdated).
export const applyCartPrices = (items, prices, isMember) => {
  const round = (value) => Math.round(value * 100) / 100;
  const changed = [];

  items.forEach((item, index) => {
    const price = prices[index];
    if (!price) {
      return;
    }

    // KSK107: the usual default (and member) price a schedule sells this line below, for the
    // kiosk cart's struck-through price and savings.
    ["original_price_no_modifier_option", "original_member_price_no_modifier_option"].forEach((key) => {
      if (price[key]) {
        item[key] = round(Number(price[key]));
      } else {
        delete item[key];
      }
    });

    const oldDefault = parseFloat(item.price_default_no_modifier_option ?? item.price_default) || 0;
    const oldMember = parseFloat(item.member_price_no_modifier_option ?? item.member_price) || oldDefault;
    const newDefault = round(Number(price.price_default_no_modifier_option) || 0);
    const newMember = round(Number(price.member_price_no_modifier_option) || newDefault);
    const shown = () => (isMember ? parseFloat(item.member_price) || 0 : parseFloat(item.price_default) || 0);
    const oldShown = shown();
    const oldWithModifiers = parseFloat(item.price_default) || 0;
    const oldMemberWithModifiers = parseFloat(item.member_price) || oldDefault;
    const modifierDelta = applyModifierPrices(item, price.modifier_prices, round);
    if (Math.abs(newDefault - oldDefault) < 0.005 && Math.abs(newMember - oldMember) < 0.005 && Math.abs(modifierDelta) < 0.005) {
      return;
    }

    item.price_default = round(oldWithModifiers + newDefault - oldDefault + modifierDelta);
    item.price_default_no_modifier_option = newDefault;
    item.member_price = round(oldMemberWithModifiers + newMember - oldMember + modifierDelta);
    item.member_price_no_modifier_option = newMember;
    const newShown = shown();
    changed.push({ item, from: oldShown, to: newShown });
  });

  return changed;
};

// order/reload hands the cart back as new line objects, in the same order: move the
// highlights of lines repriced earlier onto their new objects (same position, same item).
export const carryRepricedLines = (lines, previousItems, items) =>
  lines
    .map((line) => {
      const next = items[previousItems.indexOf(line.item)];
      return next && next.id === line.item.id ? { ...line, item: next } : null;
    })
    .filter(Boolean);
